import React, { useEffect, useState } from "react";
import { Shield, Award, Drama } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import CollapsibleCard from "@/components/CollapsibleCard";
import api, { formatApiError } from "@/lib/api";

const ROWS = [
  { key: "lettering", label: "Lettering Points", icon: Shield, hint: "Members submit lettering achievements for your approval." },
  { key: "merit", label: "Merit System", icon: Award, hint: "Merit and demerit cases, member requests and conferences." },
];

// Team-wide Lettering Points / Merit System switches (Theatre plan only).
export function useTeamFeatures() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/coach/team-features").then(({ data }) => setF(data)).catch(() => {}); }, []);
  const toggle = (key, label) => async (val) => {
    setF((p) => ({ ...p, [key]: val }));
    try {
      const { data } = await api.put("/coach/team-features", { [key]: val });
      setF(data);
      window.dispatchEvent(new Event("rpg:flags-refresh"));
      toast.success(`${label} ${val ? "turned on" : "turned off"} for your team`);
    } catch (e) { setF((p) => ({ ...p, [key]: !val })); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return [f, toggle];
}

export function TheatreFeatureSwitches({ f, toggle }) {
  return (
    <div className="space-y-2">
      {ROWS.map((r) => (
        <label key={r.key} className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3" data-testid={`theatre-feature-row-${r.key}`}>
          <span className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-slate-600 shadow-sm"><r.icon className="h-4 w-4" /></span>
            <span>
              <span className="block text-sm font-semibold text-slate-800">{r.label}</span>
              <span className="block text-xs text-slate-500">{r.hint}</span>
            </span>
          </span>
          <Switch checked={!!f?.[r.key]} onCheckedChange={toggle(r.key, r.label)} data-testid={`theatre-feature-toggle-${r.key}`} />
        </label>
      ))}
    </div>
  );
}

// Director's Profile → Settings: shown only on the Theatre plan.
export default function TheatreFeatureToggles() {
  const [f, toggle] = useTeamFeatures();
  if (!f?.theatre) return null;
  return (
    <CollapsibleCard title="Program features" icon={Drama} testId="theatre-features-card">
      <p className="mb-4 text-sm text-slate-500">Your team is on the Theatre plan. Turn off features your program doesn't use — they're hidden for you, your members and parents, and past records come back if you turn them on again.</p>
      <TheatreFeatureSwitches f={f} toggle={toggle} />
    </CollapsibleCard>
  );
}
